'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { BigOfferCard } from './BigOfferCard';
import { SmallOfferCard } from './SmallOfferCard';
import type { OfferWidgetResolved } from './types';
import { headingTag } from '@/lib/render/headingTag';
import { resolvePhonePair } from '@/lib/render/phonePair';
import {
  useRenderContext,
  useSubstitute,
} from '@/lib/render/RenderContextProvider';
import { Text } from '@/lib/render/Text';

export function OfferWidgetComponent({
  resolved,
}: {
  resolved: OfferWidgetResolved;
}) {
  const sub = useSubstitute();
  const { imageUrl, entities } = useRenderContext();
  const offers = resolved.offers ?? [];
  const isSmall = resolved.displayStyle === 'small';
  // Editors type `{domain.phone}` / `{domain.matelsoPhoneLabel}` into the
  // widget's pair (SH-548). Unsubstituted, the label reached the visitor
  // verbatim and Matelso looked it up as a number. A token that stays
  // unresolved falls through to the Standort's or the page's pair (70.07).
  const { phone, matelsoPhoneLabel } = resolvePhonePair(
    {
      phone: resolved.phone ? sub(resolved.phone) : undefined,
      matelsoPhoneLabel: resolved.matelsoPhoneLabel
        ? sub(resolved.matelsoPhoneLabel)
        : undefined,
    },
    {
      phone: entities.location?.phone,
      matelsoPhoneLabel: entities.location?.matelsoPhoneLabel as
        string | undefined,
    },
    entities.domain,
  );

  if (offers.length === 0) {
    return null;
  }

  if (isSmall) {
    const heading = headingTag(resolved.titlePower ?? '3', resolved.visualOnly);
    return (
      // `pt-8` rather than the `pt-4` default: the widget renders a *set* of
      // offers held apart by `space-y-6`, and at `pt-4` the first card had less
      // air above it than its siblings. Matching the `l` step of the shared
      // spacing scale (70.12-Widget-Spacing) makes the set read as one group.
      <section id={resolved.anchor} className="scroll-mt-20 pt-8">
        {/* `mx-auto` because `max-w-4xl` is a *measure*: narrower than the
            page on purpose, so it has to be centred in the space the
            wrapper gave it (70.14-Widget-Width § 5). */}
        <div className="mx-auto max-w-4xl">
          {resolved.title && (
            <Text
              as={heading.as}
              field="title"
              className={classNames(heading.className, 'mb-6 text-center')}
            >
              {resolved.title}
            </Text>
          )}
          <div className="space-y-4">
            {offers.map((offer) => (
              <SmallOfferCard
                key={offer.id}
                offer={offer}
                sub={sub}
                phoneTracking={resolved.phoneTracking}
                widgetTitle={resolved.title}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id={resolved.anchor} className="scroll-mt-20 space-y-6 pt-8">
      {offers.map((offer) => (
        <BigOfferCard
          key={offer.id}
          offer={offer}
          sub={sub}
          imageUrl={imageUrl}
          phone={phone}
          matelsoPhoneLabel={matelsoPhoneLabel}
          phoneTracking={resolved.phoneTracking}
          titlePower={resolved.titlePower}
          visualOnly={resolved.visualOnly}
        />
      ))}
    </section>
  );
}
